import { useState, useRef, useEffect, cloneElement } from 'react'
import { TITLE_BLUE } from '../data/site.js'
import reasonQuality from '../assets/reasons/1.webp'
import reasonSecurity from '../assets/reasons/2.webp'
import reasonOpen from '../assets/reasons/3.webp'
import reasonMaintenance from '../assets/reasons/4.webp'
import { Link } from 'react-router-dom'
import ImpactMetrics from './ImpactMetrics.jsx'
import lowerCostImg from '../assets/Homepage/lower-cost-90-v2.jpg'

/* Editorial divider — thin gradient rule either side of a glowing centre
   diamond. Shared by both section headings so they stay identical.
   Depends on the vc-div-glow-pulse keyframes declared in ValueCarousel. */
function SectionDivider({ className = 'mt-8' }) {
  return (
    <div className={`mx-auto flex items-center justify-center gap-4 ${className}`} aria-hidden="true">
      <span
        className="block h-px w-24 sm:w-32"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(56,189,248,0.55), rgba(168,85,247,0.55))',
          animation: 'vc-div-glow-pulse 3.6s ease-in-out 2s infinite',
        }}
      />
      <span className="relative inline-flex items-center justify-center">
        <span
          aria-hidden
          className="absolute size-5 rotate-45 rounded-[2px]"
          style={{
            background: 'linear-gradient(135deg, #38bdf8, #a855f7)',
            filter: 'blur(6px)',
            opacity: 0.55,
          }}
        />
        <span
          aria-hidden
          className="relative block size-2.5 rotate-45 rounded-[1px]"
          style={{
            background: 'linear-gradient(135deg, #38bdf8, #a855f7)',
            boxShadow: '0 0 12px rgba(168,85,247,0.7)',
          }}
        />
      </span>
      <span
        className="block h-px w-24 sm:w-32"
        style={{
          background: 'linear-gradient(90deg, rgba(168,85,247,0.55), rgba(232,121,249,0.55), transparent)',
          animation: 'vc-div-glow-pulse 3.6s ease-in-out 2.4s infinite',
        }}
      />
    </div>
  )
}

/* ============================================================
   AdlcTimeline — the six ADLC stages as a straight-line timeline.
   Replaces the raster lifecycle diagram: horizontal on desktop,
   vertical on tablet and mobile, with the same icons and labels.
   ============================================================ */
const ADLC_STAGES = [
  {
    label: 'Requirements\u00a0& Planning',
    icon: (
      <>
        <rect x="6" y="4" width="12" height="17" rx="2" />
        <path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" />
        <path d="M9 10l1.4 1.4L13 8.8M9 15l1.4 1.4L13 13.8" />
      </>
    ),
  },
  {
    label: 'AI-Powered Design',
    icon: (
      <>
        <path d="M12 4.5a3 3 0 0 0-5.7 1.3A2.8 2.8 0 0 0 4.6 9a2.9 2.9 0 0 0 .8 4.4A2.9 2.9 0 0 0 7 18.4 2.9 2.9 0 0 0 12 19.5z" />
        <path d="M12 4.5a3 3 0 0 1 5.7 1.3A2.8 2.8 0 0 1 19.4 9a2.9 2.9 0 0 1-.8 4.4A2.9 2.9 0 0 1 17 18.4 2.9 2.9 0 0 1 12 19.5z" />
        <path d="M12 4.5v15" />
        <circle cx="8.6" cy="9" r="1" />
        <circle cx="15.4" cy="13.4" r="1" />
      </>
    ),
  },
  {
    label: 'Code Generation',
    icon: (
      <>
        <rect x="3" y="4.5" width="18" height="15" rx="2" />
        <path d="M3 8.5h18" />
        <circle cx="6" cy="6.5" r="0.5" />
        <circle cx="8" cy="6.5" r="0.5" />
        <path d="M9.5 12l-2 2 2 2M14 12l2 2-2 2" />
      </>
    ),
  },
  {
    label: 'Automated Testing',
    icon: (
      <>
        <path d="M20 12.6V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
        <path d="M8.5 12l2.6 2.6L17 8.7" />
        <circle cx="18.6" cy="5.4" r="2.6" />
      </>
    ),
  },
  {
    label: 'Continuous Monitoring',
    icon: (
      <>
        <rect x="3" y="4.5" width="18" height="12" rx="2" />
        <path d="M9 20.5h6M12 16.5v4" />
        <path d="M6.8 13l3-3.2 2.2 2.1 3.4-3.8" />
        <path d="M15.4 8.1h1.9v1.9" />
      </>
    ),
  },
  {
    label: 'Feedback\u00a0& Improvement',
    icon: (
      <>
        <path d="M4 5.5h11a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H8.5L5 16.5V13.5H4a0 0 0 0 1 0 0z" />
        <path d="M2.5 7.5v6a2 2 0 0 0 2 2" />
        <path d="M9 9.2h5" />
        <path d="M19 9.5h.5a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H19l-2.5 2.5V17.5" />
      </>
    ),
  },
]

function AdlcTimeline() {
  const ref = useRef(null)
  const [live, setLive] = useState(false)

  /* Play the sequence once, when the strip actually reaches the viewport —
     a timeline that has already finished animating before you scroll to it
     is just a static graphic. */
  useEffect(() => {
    const node = ref.current
    if (!node) return undefined
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setLive(true)
      return undefined
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setLive(true)
          io.disconnect()
        }
      },
      { threshold: 0.25 }
    )
    io.observe(node)

    /* Failsafe. The pre-animation state hides the nodes, so if the observer
       never reports — background tab, a browser that throttles it, anything
       unexpected — the timeline would sit invisible. Play it regardless after
       a short grace period; content visibility must not depend on an effect
       firing. */
    const failsafe = setTimeout(() => {
      setLive(true)
      io.disconnect()
    }, 1600)

    return () => {
      clearTimeout(failsafe)
      io.disconnect()
    }
  }, [])

  return (
    <div ref={ref} className="adlc mb-14 lg:mb-20" data-live={live ? 'true' : 'false'}>
      <style>{`
        /* The rail draws itself, then a light travels along it. Transform and
           opacity only — no layout-triggering properties in the loop. */
        @keyframes adlc-draw-x { from { transform: scaleX(0) } to { transform: scaleX(1) } }
        @keyframes adlc-draw-y { from { transform: scaleY(0) } to { transform: scaleY(1) } }
        @keyframes adlc-node-in {
          0%   { opacity: 0; transform: translateY(14px) scale(.82) }
          60%  { opacity: 1; transform: translateY(0) scale(1.06) }
          100% { opacity: 1; transform: translateY(0) scale(1) }
        }
        @keyframes adlc-text-in {
          from { opacity: 0; transform: translateY(8px) }
          to   { opacity: 1; transform: translateY(0) }
        }
        @keyframes adlc-halo {
          0%   { opacity: .55; transform: scale(.75) }
          70%  { opacity: 0;   transform: scale(1.75) }
          100% { opacity: 0;   transform: scale(1.75) }
        }
        @keyframes adlc-comet-x {
          0%   { opacity: 0; left: 0 }
          8%   { opacity: 1 }
          92%  { opacity: 1 }
          100% { opacity: 0; left: 100% }
        }
        @keyframes adlc-comet-y {
          0%   { opacity: 0; top: 0 }
          8%   { opacity: 1 }
          92%  { opacity: 1 }
          100% { opacity: 0; top: 100% }
        }

        .adlc .adlc-rail    { transform-origin: top center; transform: scaleY(0) }
        .adlc .adlc-node,
        .adlc .adlc-label   { opacity: 0 }
        .adlc .adlc-comet   { opacity: 0 }

        .adlc[data-live="true"] .adlc-rail {
          animation: adlc-draw-y 1.1s cubic-bezier(.65,0,.35,1) both;
        }
        .adlc[data-live="true"] .adlc-node {
          animation: adlc-node-in .62s cubic-bezier(.22,1.2,.36,1) both;
          animation-delay: calc(.28s + var(--i) * .11s);
        }
        .adlc[data-live="true"] .adlc-label {
          animation: adlc-text-in .5s ease-out both;
          animation-delay: calc(.38s + var(--i) * .11s);
        }
        .adlc[data-live="true"] .adlc-halo {
          animation: adlc-halo .9s ease-out both;
          animation-delay: calc(.30s + var(--i) * .11s);
        }
        .adlc[data-live="true"] .adlc-comet {
          animation: adlc-comet-y 3.6s linear 1.5s infinite;
        }

        @media (min-width: 1024px) {
          .adlc .adlc-rail { transform-origin: left center; transform: scaleX(0) }
          .adlc[data-live="true"] .adlc-rail { animation-name: adlc-draw-x }
          .adlc[data-live="true"] .adlc-comet { animation-name: adlc-comet-x }
        }

        /* Pointer affordance: the node lifts and its ring lights up. */
        .adlc-step .adlc-node { transition: transform .35s cubic-bezier(.22,1,.36,1), border-color .35s, box-shadow .35s }
        .adlc-step:hover .adlc-node {
          transform: translateY(-4px) scale(1.07);
          border-color: rgba(0,142,255,.95);
          box-shadow: 0 0 0 6px rgba(0,142,255,.10), 0 14px 30px -12px rgba(0,142,255,.75);
        }
        .adlc-step .adlc-num { transition: color .35s }
        .adlc-step:hover .adlc-num { color: #008eff }

        @media (prefers-reduced-motion: reduce) {
          .adlc .adlc-rail  { transform: none !important; animation: none !important }
          .adlc .adlc-node,
          .adlc .adlc-label { opacity: 1 !important; animation: none !important }
          .adlc .adlc-comet { display: none }
        }
      `}</style>

      <ol
        className="
          relative mx-auto flex max-w-6xl list-none flex-col gap-8
          lg:flex-row lg:items-start lg:justify-between lg:gap-4
        "
      >
        {/* Rail — vertical behind the icon column, horizontal behind the row */}
        <span
          aria-hidden="true"
          className="adlc-rail absolute left-[30px] top-3 bottom-3 w-px bg-gradient-to-b from-azure-400/10 via-azure-400/60 to-azure-400/10 lg:left-0 lg:right-0 lg:top-[30px] lg:bottom-auto lg:h-px lg:w-auto lg:bg-gradient-to-r"
        />
        {/* Light travelling the rail */}
        <span
          aria-hidden="true"
          className="adlc-comet absolute left-[30px] top-3 h-10 w-px -translate-x-1/2 rounded-full lg:left-0 lg:top-[30px] lg:h-px lg:w-16 lg:-translate-x-0"
          style={{
            background:
              'linear-gradient(180deg, transparent, #008eff, transparent)',
            boxShadow: '0 0 14px 2px rgba(0,142,255,.55)',
          }}
        />

        {ADLC_STAGES.map((stage, i) => (
          <li
            key={stage.label}
            style={{ '--i': i }}
            className="adlc-step relative flex items-center gap-4 lg:w-full lg:flex-col lg:gap-3 lg:text-center"
          >
            <span className="relative grid shrink-0 place-items-center">
              {/* one-shot halo as the node lands */}
              <span
                aria-hidden="true"
                className="adlc-halo pointer-events-none absolute size-[60px] rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(0,142,255,.55), transparent 70%)' }}
              />
              <span className="adlc-node grid size-[60px] place-items-center rounded-full border border-azure-400/60 bg-ink-950 text-azure-900 shadow-[0_0_22px_-6px_rgba(0,142,255,0.75)]">
                <svg
                  viewBox="0 0 24 24"
                  className="size-7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  {stage.icon}
                </svg>
              </span>
            </span>

            <span className="adlc-label min-w-0 lg:px-1">
              <span className="adlc-num block font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-azure-800">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="mt-0.5 block text-sm font-semibold uppercase leading-snug tracking-wide text-white sm:text-[15px]">
                {stage.label}
              </span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}

/* ============================================================
   CostHighlight — the headline saving, mirroring the layout and
   type scale of the "Always-on support" callout further down.
   ============================================================ */
function CostHighlight() {
  return (
    <>
      <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
      {/* LEFT — cost comparison illustration. It carries the 90% and the rupee
          figures, so the copy column beside it never repeats them. */}
      <div className="relative">
        <img
          src={lowerCostImg}
          alt="Spectranox delivering the same build at 90% lower cost"
          loading="lazy"
          draggable="false"
          className="mx-auto block w-full rounded-3xl border border-slate-200 object-cover shadow-[0_30px_80px_-40px_rgba(56,189,248,0.4)] sm:max-w-[320px] lg:max-w-none"
        />
      </div>

      {/* RIGHT — the claim, why it holds, and the way in */}
      <div className="relative">
        <h3 className="section-title-sm font-bold text-white">
          Enterprise-grade software,{' '}
          <span style={{ color: TITLE_BLUE }}>built at startup cost.</span>
        </h3>

        <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300">
          Most first builds die on the quote. Our{' '}
          <span className="font-semibold text-white">AIDLC</span> lifecycle cuts out the
          months of manual setup that agencies bill you for, so you end up with the same
          architecture and the same engineers for a lot less money.
        </p>

        {/* One short claim each. Detail belongs in the paragraph above; a
            bullet that wraps stops reading as a bullet. */}
        <ul className="mt-7 space-y-3.5">
          {[
            'A fixed price, agreed before we start.',
            'Senior engineers on every build.',
            'The code is yours from day one.',
          ].map((point) => (
            <li key={point} className="flex items-center gap-3.5">
              <span
                aria-hidden="true"
                className="grid size-[22px] shrink-0 place-items-center rounded-full"
                /* colour inline: the light-theme sheet overrides text-white here,
                   which would render the tick dark slate on the blue disc. */
                style={{ background: TITLE_BLUE, color: '#ffffff', boxShadow: '0 4px 12px -3px rgba(29,78,216,0.55)' }}
              >
                <svg viewBox="0 0 24 24" className="size-3" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12l5 5L20 7" />
                </svg>
              </span>
              <span className="text-[17px] font-semibold leading-snug tracking-tight text-white sm:text-lg">
                {point}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
          <Link
            to="/contact"
            className="btn-on-solid group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5"
            style={{ background: TITLE_BLUE, boxShadow: '0 16px 34px -14px rgba(29,78,216,0.85)' }}
          >
            Talk to us
            <svg viewBox="0 0 24 24" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
          <p className="text-sm leading-snug text-slate-300">
            Tell us what you're building.
            <br className="hidden sm:block" />
            We'll send back a scope and a price.
          </p>
        </div>
      </div>
      </div>
    </>
  )
}

/* VcStage — wraps its single child and adds data-revealed="true" the first
   time the child scrolls into view. Drives the cinematic vc-* keyframes. */
function VcStage({ children }) {
  const ref = useRef(null)
  const [revealed, setRevealed] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setRevealed(true)
      return
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true)
          io.disconnect()
        }
      },
      { threshold: 0.18, rootMargin: '0px 0px -6% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return cloneElement(children, {
    ref,
    'data-revealed': revealed ? 'true' : 'false',
  })
}

/* ============================================================
   ValueCarousel — interactive "7 reasons" presenter. A central
   draggable / clickable hero card flanked by two animated side
   cards that update as the index changes. Prev/Next + numbered
   1–7 pagination. Keyboard, swipe and click all work.
   ============================================================ */

function Commitments() {
  /* Four commitments, four pictures of the thing being promised. Three of
     these used to pull generic stock photographs off Unsplash — a laptop, a
     circuit board, a meeting — and the fourth had no picture at all, so the
     row broke its own rhythm on the last card. */
  const items = [
    { kicker: 'Warranty',    num: '30',  unit: 'Days',   title: 'We fix our own bugs',      desc: "If something we built breaks, we fix it. You don't pay, and you don't have to argue about it.", image: reasonQuality,     accent: '#38bdf8' },
    { kicker: 'Coverage',    num: '1',   unit: 'Year',   title: 'A year of security cover', desc: 'We handle patching, monitoring and dependency updates for a full year after launch.',              image: reasonSecurity,    accent: '#008eff' },
    { kicker: 'Process',     num: '100', unit: '% open', title: 'You watch us build it',    desc: 'Repo access from day one and a working demo every week. Nothing happens behind a curtain.',        image: reasonOpen,        accent: '#52b4ff' },
    { kicker: 'Maintenance', num: '−70', unit: '%',      title: 'Reduced maintenance cost', desc: 'Clean architecture, current dependencies and a documented handover. Less breaks, and what does is quicker to fix.', image: reasonMaintenance, accent: '#0062ad' },
  ]
  return (
    <VcStage>
      <div className="vc-commit mt-8 lg:mt-2">
        <style>{`
          @keyframes vc-rule-sweep {
            0%   { transform: scaleX(0); opacity: 0; }
            100% { transform: scaleX(1); opacity: 1; }
          }
          @keyframes vc-commit-rise {
            0%   { opacity: 0; transform: translateY(40px) scale(0.95); filter: blur(8px); }
            60%  { filter: blur(0); }
            100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
          }
          @keyframes vc-media-zoom {
            0%   { opacity: 0; transform: scale(1.12); filter: blur(10px); }
            100% { opacity: 1; transform: scale(1); filter: blur(0); }
          }
          @keyframes vc-num-rise {
            0%   { opacity: 0; transform: translateY(28px); letter-spacing: -0.01em; }
            100% { opacity: 1; transform: translateY(0); letter-spacing: -0.04em; }
          }
          @keyframes vc-line-grow {
            0%   { width: 0; opacity: 0; }
            100% { width: 2.5rem; opacity: 1; }
          }
          @keyframes vc-text-rise {
            0%   { opacity: 0; transform: translateY(12px); filter: blur(4px); }
            100% { opacity: 1; transform: translateY(0); filter: blur(0); }
          }
          @keyframes vc-card-glow {
            0%   { opacity: 0; transform: scale(0.55); }
            55%  { opacity: 1; transform: scale(1.1); }
            100% { opacity: 0.7; transform: scale(1); }
          }
          .vc-commit .vc-rule,
          .vc-commit .vc-cell,
          .vc-commit .vc-media,
          .vc-commit .vc-num,
          .vc-commit .vc-hr,
          .vc-commit .vc-title,
          .vc-commit .vc-desc,
          .vc-commit .vc-meta { opacity: 0; }
          .vc-commit[data-revealed="true"] .vc-rule {
            transform-origin: left;
            animation: vc-rule-sweep 1.1s cubic-bezier(0.65,0,0.35,1) 0.05s both;
          }
          .vc-commit[data-revealed="true"] .vc-cell {
            animation: vc-commit-rise 0.95s cubic-bezier(0.22,1,0.36,1) calc(0.15s + var(--c, 0s)) both;
          }
          .vc-commit[data-revealed="true"] .vc-media {
            animation: vc-media-zoom 1.05s cubic-bezier(0.22,1,0.36,1) calc(0.30s + var(--c, 0s)) both;
          }
          .vc-commit[data-revealed="true"] .vc-meta {
            animation: vc-text-rise 0.6s ease-out calc(0.40s + var(--c, 0s)) both;
          }
          .vc-commit[data-revealed="true"] .vc-num {
            animation: vc-num-rise 0.9s cubic-bezier(0.22,1,0.36,1) calc(0.45s + var(--c, 0s)) both;
          }
          .vc-commit[data-revealed="true"] .vc-hr {
            animation: vc-line-grow 0.7s cubic-bezier(0.65,0,0.35,1) calc(0.6s + var(--c, 0s)) both;
          }
          .vc-commit[data-revealed="true"] .vc-title {
            animation: vc-text-rise 0.55s ease-out calc(0.70s + var(--c, 0s)) both;
          }
          .vc-commit[data-revealed="true"] .vc-desc {
            animation: vc-text-rise 0.55s ease-out calc(0.82s + var(--c, 0s)) both;
          }
          .vc-commit[data-revealed="true"] .vc-cell-glow {
            animation: vc-card-glow 2.4s ease-in-out calc(0.5s + var(--c, 0s)) both;
          }
        `}</style>

        {/* sweeping rule above the badges */}
        <div className="vc-rule mb-5 h-px w-full bg-gradient-to-r from-transparent via-white/30 to-transparent" aria-hidden="true" />

        {/* Full-bleed on phones: the container gutter is cancelled and the side
            borders and corner radius drop away, so the cards run edge to edge. */}
        <div className="-mx-5 grid grid-cols-1 gap-px overflow-hidden rounded-none border-y border-x-0 border-white/[0.07] bg-white/[0.04] sm:mx-0 sm:grid-cols-2 sm:rounded-2xl sm:border-x lg:grid-cols-4">
          {items.map((it, idx) => (
            <article
              key={it.title}
              className="vc-cell group relative flex flex-col overflow-hidden bg-ink-950 p-6 transition-colors duration-300 hover:bg-white/[0.015] lg:p-7"
              style={{ '--c': `${idx * 0.12}s` }}
            >
              {/* accent glow that pulses once during reveal */}
              <span
                aria-hidden
                className="vc-cell-glow pointer-events-none absolute -top-12 -right-12 h-40 w-40 rounded-full blur-3xl"
                style={{ background: it.accent, opacity: 0 }}
              />

              {/* The label used to appear only on the one card that had no
                  picture, which made it read as a caption for the missing
                  image rather than as the name of the commitment. It now sits
                  above every card, so all four open the same way. */}
              <div className="vc-meta mb-4 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.3em] text-slate-500">
                <span>{String(idx + 1).padStart(2, '0')}</span>
                <span aria-hidden className="h-px w-4" style={{ background: it.accent, opacity: 0.6 }} />
                <span className="text-slate-400">{it.kicker}</span>
              </div>

              <div className="vc-media overflow-hidden rounded-[16px] border border-white/10 ring-1 ring-white/5">
                <img
                  src={it.image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  draggable="false"
                  className="block w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                  style={{ aspectRatio: '16/9' }}
                />
              </div>
              <div className="mt-7 flex items-baseline gap-1.5">
                <span
                  className="vc-num font-display font-semibold leading-none tracking-tight text-white"
                  style={{ fontSize: 'clamp(2.6rem, 4.2vw, 3.4rem)', letterSpacing: '-0.04em' }}
                >
                  {it.num}
                </span>
                {/* Sized to sit legibly beside the figure rather than as a
                    tiny monospace tick-label. */}
                <span className="vc-num font-sans text-[17px] font-semibold tracking-normal text-slate-400 sm:text-[19px]">
                  {it.unit}
                </span>
              </div>
              <div
                aria-hidden
                className="vc-hr mt-5 h-px origin-left transition-all duration-500 group-hover:w-20"
                style={{ background: `linear-gradient(90deg, ${it.accent}, rgba(255,255,255,0.4))`, width: 0 }}
              />
              <div className="mt-4">
                {/* font-sans overrides the global h1–h4 font-display rule, so the
                    card copy reads in the plain body face rather than the
                    geometric display one. */}
                {/* h3, not h4 — the section heading is an h2 and skipping a
                    level breaks the document outline. */}
                <h3 className="vc-title font-sans text-[18px] font-semibold leading-snug tracking-normal text-white sm:text-[19px]">
                  {it.title}
                </h3>
                <p className="vc-desc mt-2.5 font-sans text-[14.5px] leading-relaxed text-slate-400 sm:text-[15px]">
                  {it.desc}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </VcStage>
  )
}


/* ============================================================
   ValueCarousel — "Why clients choose Spectranox".
   The section heading, the animated SDLC vs ADLC comparison that
   carries the impact metrics, and the ironclad commitments row.
   ============================================================ */
/* The lifecycle timeline and the cost comparison, as a standalone band. Split
   out of ValueCarousel so the page can place it independently of the
   "Promises we put in writing" section it used to sit directly above. */
export function AdlcCostBand() {
  return (
    <section
      className="relative overflow-hidden bg-ink-950 py-10 sm:py-14 lg:py-16"
      aria-label="90% lower cost with the ADLC lifecycle"
    >
      {/* Divider keyframes travel with the band that uses them. */}
      <style>{`
        @keyframes vc-div-glow-pulse { 0%, 100% { opacity: 0.55 } 50% { opacity: 0.9 } }
      `}</style>
      <div className="relative mx-auto w-full max-w-[1240px] px-5 sm:px-8">
        <CostHighlight />
      </div>
    </section>
  )
}

export default function ValueCarousel() {
  const accent = '#a855f7'

  return (
    <>
      <style>{`
        @keyframes vc-div-glow-pulse { 0%, 100% { opacity: 0.55 } 50% { opacity: 0.9 } }
      `}</style>

    <section className="relative overflow-hidden bg-ink-950 py-10 sm:py-14 lg:py-16" aria-label="Reasons why clients love Spectranox">
      <style>{`
        @keyframes ec-wave { 0% { transform: translateX(0); } 100% { transform: translateX(-24px); } }
        @keyframes ec-signal {
          0%, 100% { transform: scaleY(0.4); opacity: 0.35; }
          50%      { transform: scaleY(1);   opacity: 1; }
        }
        @keyframes ec-arc-draw { to { stroke-dashoffset: 0; } }
        @keyframes ec-fill-rise { from { transform: translateY(100%); } to { transform: translateY(35%); } }
      `}</style>

      {/* ambient backdrop */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute -top-32 left-1/2 h-[500px] w-[820px] -translate-x-1/2 rounded-full blur-[140px]"
          style={{ background: `${accent}1f` }}
        />
        <div className="absolute bottom-0 left-0 h-[280px] w-[400px] rounded-full bg-[#0c4a6e]/20 blur-[100px]" />
      </div>

      <div className="relative mx-auto w-full max-w-[1240px] px-5 sm:px-8">
        {/* Section heading — refined editorial */}
        <div className="mx-auto max-w-3xl text-center">
          {/* Two fixed lines at every width: each half is its own non-wrapping
              block, and below sm the size follows the viewport so neither can
              overflow. "Reasons why clients" measures ~9.4x the font size. */}
          <h2 className="font-display text-[clamp(1.375rem,7.2vw,2.125rem)] font-semibold leading-[1.02] tracking-[-0.02em] text-white sm:text-[44px] lg:text-[56px]">
            <span className="block whitespace-nowrap">Reasons why clients</span>
            <span className="block whitespace-nowrap" style={{ color: TITLE_BLUE }}>
              love Spectranox
            </span>
          </h2>

          <SectionDivider />
        </div>

        {/* Ironclad commitments — 4 trust badges with engraved diagrams */}
        <Commitments />

        {/* What the AI DLC loop is worth — the three impact figures */}
        <ImpactMetrics className="mt-10 lg:mt-12" />

      </div>
    </section>
    </>
  )
}